// Base inbox query key prefix for broad invalidations
export const inboxV2QueryKeyPrefix = () => ['inbox-v2', 'pull-requests-v3'] as const;
export const inboxV2OpenPullRequestsQueryKeyPrefix = () =>
  [...inboxV2QueryKeyPrefix(), 'open'] as const;
export const inboxV2MergedPullRequestsQueryKeyPrefix = () =>
  [...inboxV2QueryKeyPrefix(), 'merged'] as const;
export const inboxV2GithubPullRequestSearchQueryKeyPrefix = () =>
  [...inboxV2QueryKeyPrefix(), 'github-search'] as const;
export const inboxV2CheckSummariesQueryKeyPrefix = () =>
  [...inboxV2QueryKeyPrefix(), 'check-summaries'] as const;
export const inboxV2AutoFixSummariesQueryKeyPrefix = () =>
  [...inboxV2QueryKeyPrefix(), 'auto-fix-summaries'] as const;
/** Snoozes belong to the user, not to an installation or a repository selection. */
export const inboxV2SnoozesQueryKey = () => [...inboxV2QueryKeyPrefix(), 'snoozes'] as const;

/**
 * Helper function to safely format repository IDs for query keys
 * Returns null if repositoryIds is undefined or empty
 */
const formatRepositoryIds = (repositoryIds?: number[]): string | null => {
  if (!repositoryIds || repositoryIds.length === 0) {
    return null;
  }
  return [...repositoryIds].sort((a, b) => a - b).join(',');
};

/**
 * DB inbox open PRs live in a dedicated cache entry so the main actionable sections can render
 * before the merged section finishes loading.
 */
export const inboxV2OpenPullRequestsQueryKey = (
  selectedInstallationId?: number | null,
  repositoryIds?: number[],
) =>
  [
    ...inboxV2OpenPullRequestsQueryKeyPrefix(),
    selectedInstallationId,
    formatRepositoryIds(repositoryIds),
  ] as const;

/**
 * DB inbox merged PRs use a separate cache entry so merged history can stream in later without
 * blocking first paint of the open-review sections.
 */
export const inboxV2MergedPullRequestsQueryKey = (
  selectedInstallationId?: number | null,
  repositoryIds?: number[],
) =>
  [
    ...inboxV2MergedPullRequestsQueryKeyPrefix(),
    selectedInstallationId,
    formatRepositoryIds(repositoryIds),
  ] as const;

export const inboxV2CheckSummariesQueryKey = (
  section: 'open' | 'merged',
  selectedInstallationId?: number | null,
  repositoryIds?: number[],
) =>
  [
    ...inboxV2CheckSummariesQueryKeyPrefix(),
    section,
    selectedInstallationId,
    formatRepositoryIds(repositoryIds),
  ] as const;

export const inboxV2AutoFixSummariesQueryKey = (
  selectedInstallationId?: number | null,
  repositoryIds?: number[],
) =>
  [
    ...inboxV2AutoFixSummariesQueryKeyPrefix(),
    selectedInstallationId,
    formatRepositoryIds(repositoryIds),
  ] as const;

export const inboxV2GithubPullRequestSearchQueryKey = (
  selectedInstallationId?: number | null,
  repositoryIds?: number[],
  query?: string,
) =>
  [
    ...inboxV2GithubPullRequestSearchQueryKeyPrefix(),
    selectedInstallationId,
    formatRepositoryIds(repositoryIds),
    query ?? '',
  ] as const;
